// Shared Change Order PDF generator. Same output shape as
// `generateExecutedCOPdf` in pages/COReview.jsx but without a signature
// block (used by the operator-facing CO Builder "PDF" button).
//
// Usage:
//   const doc = await generateCOPdf({co, woNumber, woTitle, clientName, siteName, siteAddress, taxRate});
//   doc.save(`${co.co_number}.pdf`);

import { format } from "date-fns";

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Mirrors `rollUpLineItems` in COReview.jsx — one row per equipment item.
// When `breakout` is true (WO show_breakdown=ON), Material + Labor render
// as separate columns; otherwise a single Total column.
function rollUpLineItems(lineItems, breakout) {
  const buckets = {};
  for (const li of lineItems || []) {
    const raw = String(li.description || "");
    const clean = raw.split(" (")[0];
    const key = clean.trim().toLowerCase() || "misc";
    const ct = (li.cost_type || "").toLowerCase();
    const qty = Number(li.quantity || 0);
    const unit = Number(li.unit_price || 0);
    const extended = qty * unit;
    const b = buckets[key] || {
      name: clean,
      part_number: li.part_number || "",
      install_description: li.install_description || "",
      quantity: 0, unit_price: 0, total: 0,
      material_total: 0, labor_total: 0, freight_total: 0,
      _mat_qty: 0,
    };
    b.total += extended;
    if (ct === "material") { b.material_total += extended; b._mat_qty += qty; }
    else if (ct === "labor") { b.labor_total += extended; }
    else if (ct === "freight") { b.freight_total += extended; }
    if (li.part_number && !b.part_number) b.part_number = li.part_number;
    if (li.install_description && !b.install_description) b.install_description = li.install_description;
    buckets[key] = b;
  }
  return Object.values(buckets).map(b => {
    const q = b._mat_qty > 0 ? b._mat_qty : (b.quantity || 1);
    return {
      name: b.name, part_number: b.part_number, install_description: b.install_description,
      quantity: q,
      material_total: b.material_total,
      labor_total: b.labor_total,
      total: b.total,
    };
  });
}

export async function generateCOPdf({
  co,
  woNumber = "",
  woTitle = "",
  clientName = "",
  siteName = "",
  siteAddress = "",
  taxRate = 0,
  coSubtotal,
  coTaxAmount,
  coTotal,
  hideTax = false,
  originalContractTotal = null,
  priorApprovedTotal = 0,
  signatureData = null,
  signedBy = "",
}) {
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  const { addCompanyHeader } = await import("@/lib/pdfHeader");

  const doc = new jsPDF();
  const headerY = addCompanyHeader(doc, 10);
  doc.setFontSize(16);
  doc.text("CHANGE ORDER", 14, headerY + 4);
  doc.setFontSize(10);
  doc.text(`CO# ${co.co_number || "—"}`, 14, headerY + 12);
  if (woNumber) doc.text(`WO# ${woNumber}`, 14, headerY + 18);
  doc.text(`Date: ${format(new Date(), "MMM d, yyyy")}`, 14, headerY + 24);

  // BILL TO / SITE / ADDRESS block (right side)
  const rightX = 200;
  let rY = headerY + 4;
  doc.setFontSize(8); doc.setTextColor(100, 100, 100); doc.setFont(undefined, "bold");
  doc.text("BILL TO", rightX, rY, { align: "right" });
  doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
  doc.text(clientName || "—", rightX, rY + 5, { align: "right" });
  rY += 12;
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("SITE", rightX, rY, { align: "right" });
  doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
  doc.text(siteName || "—", rightX, rY + 5, { align: "right" });
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("ADDRESS", rightX, rY + 11, { align: "right" });
  doc.setFontSize(9); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
  doc.text(siteAddress || "—", rightX, rY + 16, { align: "right", maxWidth: 90 });
  doc.setTextColor(0, 0, 0);

  // PROJECT
  const pY = headerY + 40;
  doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  doc.text("PROJECT", 14, pY);
  doc.setFontSize(11); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
  doc.text(woTitle || co.description || "—", 14, pY + 6);

  // Description
  if (co.description) {
    doc.setFontSize(9); doc.setTextColor(80, 80, 80);
    doc.text(`Description: ${co.description}`, 14, pY + 14, { maxWidth: 180 });
    doc.setTextColor(0, 0, 0);
  }

  // Line items — reuse the same rollup as COReview so operators see
  // the same numbers as the customer will.
  const breakout = !!co.show_breakdown;
  let items = rollUpLineItems(co.line_items || [], breakout);
  // iter310 — hide-tax change order: no tax line for the customer, the
  // tax rides inside the Material figures instead.
  const hiddenTax = hideTax ? Number(coTaxAmount ?? co.tax_amount ?? 0) : 0;
  if (hiddenTax) {
    const { foldHiddenTaxIntoRows, NO_MATERIAL_MSG } = await import("@/lib/hideTax");
    const folded = foldHiddenTaxIntoRows(items, hiddenTax, { materialKey: "material_total" });
    if (!folded.ok) throw new Error(NO_MATERIAL_MSG);
    items = folded.rows;
  }
  const headRow = breakout
    ? [["Part #", "Description", "Qty", "Material", "Freight", "Labor", "Total"]]
    : [["Part #", "Description", "Qty", "Total"]];
  const rows = items.map(li => breakout
    ? [
        li.part_number || "—",
        li.install_description ? `${li.name || ""}\n${li.install_description}` : (li.name || "—"),
        String(li.quantity ?? 1),
        fmt(li.material_total || 0),
        fmt(li.freight_total || 0),
        fmt(li.labor_total || 0),
        fmt(li.total || 0),
      ]
    : [
        li.part_number || "—",
        li.install_description ? `${li.name || ""}\n${li.install_description}` : (li.name || "—"),
        String(li.quantity ?? 1),
        fmt(li.total || 0),
      ]);
  autoTable(doc, {
    startY: headerY + 60,
    head: headRow,
    body: rows,
    theme: "striped",
    styles: { fontSize: 8 },
    headStyles: { fillColor: [51, 65, 85] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  // Totals
  let y = doc.lastAutoTable.finalY + 10;
  const sub = coSubtotal ?? co.subtotal ?? 0;
  const tax = coTaxAmount ?? co.tax_amount ?? 0;
  const total = coTotal ?? co.total ?? sub;
  doc.setFontSize(10);
  doc.text("Subtotal:", 140, y, { align: "right" });
  doc.text(fmt(hiddenTax ? Number((sub + hiddenTax).toFixed(2)) : sub), 190, y, { align: "right" });
  y += 6;
  if (taxRate > 0 && !hideTax) {
    doc.text(`Tax (${taxRate}%):`, 140, y, { align: "right" });
    doc.text(fmt(tax), 190, y, { align: "right" });
    y += 6;
  }
  doc.setFontSize(12); doc.setFont(undefined, "bold");
  doc.text("Change Order Total:", 140, y, { align: "right" });
  doc.text(fmt(total), 190, y, { align: "right" });
  doc.setFont(undefined, "normal");

  // Contract Summary (only when we know the original contract value)
  if (originalContractTotal != null) {
    y += 14;
    const revised = (originalContractTotal || 0) + (priorApprovedTotal || 0) + (total || 0);
    doc.setDrawColor(200, 200, 200);
    doc.roundedRect(14, y - 5, 182, 32, 2, 2);
    doc.setFontSize(9); doc.setTextColor(100, 100, 100); doc.setFont(undefined, "bold");
    doc.text("CONTRACT SUMMARY", 18, y);
    doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text("Original Contract Value:", 18, y + 6);
    doc.text(fmt(originalContractTotal), 190, y + 6, { align: "right" });
    doc.text("Prior Approved COs:", 18, y + 12);
    doc.text(fmt(priorApprovedTotal), 190, y + 12, { align: "right" });
    doc.text(`This CO (${co.co_number || "—"}):`, 18, y + 18);
    doc.text(fmt(total), 190, y + 18, { align: "right" });
    doc.setFont(undefined, "bold");
    doc.text("Revised Contract Value:", 18, y + 24);
    doc.text(fmt(revised), 190, y + 24, { align: "right" });
    doc.setFont(undefined, "normal");
    y += 32;
  }

  // Signature (only for executed COs — passed by COReview)
  if (signatureData) {
    y += 14;
    doc.setFontSize(10);
    doc.text("Customer Signature:", 14, y);
    y += 4;
    doc.addImage(signatureData, "PNG", 14, y, 60, 25);
    y += 28;
    doc.text(`Signed by: ${signedBy || "N/A"}`, 14, y);
    y += 5;
    doc.text(`Date: ${format(new Date(), "MMM d, yyyy h:mm a")}`, 14, y);
  }

  return doc;
}
